import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useHistory } from "react-router-dom";
import { useAsync } from "react-async";
import { observer } from "mobx-react-lite";
import {
  AzureMap,
  AzureMapDataSourceProvider,
  AzureMapFeature,
  AzureMapLayerProvider,
  AzureMapsProvider,
  ControlOptions,
  IAzureCustomControls,
  IAzureMapControls,
  IAzureMapOptions,
  IAzureMapImageSprite,
} from "react-azure-maps";
import { AuthenticationType, Popup, MapDataEvent } from "azure-maps-control";

import "./SewerObjectsMap.scss";
import LoadingSpinner from "../loading-spinner/LoadingSpinner";
import { SewerObjectsMapStorage } from "../../services/sewer-object/SewerObjectsMapStorage";
import { configuration } from "../../services/configuration/ConfigurationService";
import {
  MapNode,
  MapNodeSelected,
  MapStructure,
  MapStructureSelected,
} from "../../components/svg/svgImageList";
import SewerObjectPopupModal from "./SewerObjectPopupModal";
import { BringDataIntoViewControl } from "./default-zoom/BringDataIntoViewControl";
import {
  CardinalPoints,
  CenterAndZoom,
  addPaddingToBoundingCardinalPoints,
  instanceOfCardinalPoints,
  instanceOfCenterAndZoom,
} from "../../helpers/MapHelpers";
import { SewerObjectType } from "../../generated/SewerObjects";

interface SewerObjectsMapProps {
  fullPage?: boolean;
  sewerObjectsMapStorage: SewerObjectsMapStorage;
}

export default observer(function SewerObjectsMap({
  fullPage = false,
  sewerObjectsMapStorage,
}: SewerObjectsMapProps) {
  const history = useHistory();
  const { data: configurationContent } = useAsync(configuration.get);
  const [mapZoomingInfo, setMapZoomingInfo] = useState<CardinalPoints | CenterAndZoom | null>();
  const popup = useMemo(() => new Popup({}), []);

  const nodesAndStructures = useMemo(
    () => sewerObjectsMapStorage.mapItems?.filter((x) => x.mapShapeType === "Point") ?? [],
    [sewerObjectsMapStorage.mapItems]
  );
  const sections = useMemo(
    () =>
      sewerObjectsMapStorage.mapItems?.filter(
        (x) => x.mapShapeType === "LineString" && x.objectType === SewerObjectType.Section
      ) ?? [],
    [sewerObjectsMapStorage.mapItems]
  );

  const azureMapOptions: IAzureMapOptions = useMemo(() => {
    return {
      authOptions: {
        authType: AuthenticationType.anonymous,
        clientId: configurationContent?.clientIdMapsAccount,
        getToken: function (resolve, _reject, _map) {
          sewerObjectsMapStorage?.getToken().then((token) => resolve(token));
        },
      },
      center: instanceOfCenterAndZoom(mapZoomingInfo)
        ? Object.values(mapZoomingInfo.center || {})
        : sewerObjectsMapStorage.mapCenter,
      zoom: instanceOfCenterAndZoom(mapZoomingInfo) ? mapZoomingInfo.zoom : 15,
      view: "Auto",
      ...(instanceOfCardinalPoints(mapZoomingInfo) && {
        bounds: Object.values(mapZoomingInfo || {}),
      }),
      style: sewerObjectsMapStorage.currentMapStyles,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configurationContent?.clientIdMapsAccount,
    sewerObjectsMapStorage,
    sewerObjectsMapStorage.mapCenter,
    mapZoomingInfo,
  ]);

  const selectedShapeRef = useRef<any>(undefined);
  const mapDataSourceRef = useRef<any>(undefined);

  const deselectPreviousShape = useCallback(() => {
    if (!selectedShapeRef.current) return;

    var prevMapItem = mapDataSourceRef.current.getShapeById(selectedShapeRef.current.getId());
    prevMapItem.setProperties({ ...prevMapItem.getProperties(), isSelected: false });
    selectedShapeRef.current = undefined;
  }, []);

  const mapItemClick = useCallback(
    (e: any) => {
      if (selectedShapeRef.current) deselectPreviousShape();

      const mapItem = e.shapes[0];
      selectedShapeRef.current = mapItem;
      mapItem.setProperties({ ...mapItem.getProperties(), isSelected: true });

      const c = mapItem.getCoordinates();
      popup.setOptions({
        position:
          mapItem.getType() === "LineString"
            ? [
                c.reduce((_: number, v: number) => _ + v[0], 0) / c.length,
                c.reduce((_: number, v: number) => _ + v[1], 0) / c.length,
              ]
            : c,
        content: renderToStaticMarkup(
          <SewerObjectPopupModal
            selectedShape={selectedShapeRef?.current}
            historyPath={history.location.pathname}
          />
        ),
      });
      popup.open();
    },
    [deselectPreviousShape, history.location.pathname, popup]
  );

  useEffect(() => {
    if (!mapDataSourceRef.current) return;
    if (selectedShapeRef.current) popup.close();
    if (!sewerObjectsMapStorage.defaultSelectedItem) return;

    var itemToSelect = mapDataSourceRef.current
      .getShapes()
      .find(
        (s: any) =>
          Number(s.getProperties().mapObjectId) ===
          Number(sewerObjectsMapStorage.defaultSelectedItem)
      );
    if (!itemToSelect) return;
    if (!itemToSelect.getBounds()) return;

    selectedShapeRef.current = itemToSelect;
    itemToSelect.setProperties({ ...itemToSelect.getProperties(), isSelected: true });

    mapDataSourceRef.current.map.setCamera({
      bounds: itemToSelect.getBounds(),
      padding: 200,
    });
  }, [popup, sewerObjectsMapStorage.defaultSelectedItem]);

  const controls: IAzureMapControls[] = [
    {
      controlName: "StyleControl",
      controlOptions: {
        mapStyles: [
          "road",
          "satellite_road_labels",
          "road_shaded_relief",
          "grayscale_dark",
          "grayscale_light",
        ],
      },
      options: { position: "top-right" } as ControlOptions,
    },
    {
      controlName: "ZoomControl",
      options: { position: "top-right" } as ControlOptions,
    },
    {
      controlName: "PitchControl",
      options: { position: "top-right" } as ControlOptions,
    },
    {
      controlName: "CompassControl",
      options: { position: "top-right" } as ControlOptions,
    },
  ];

  const sewerObjectsImageSprites: IAzureMapImageSprite[] = [
    { id: "mapNode", icon: MapNode },
    { id: "mapNodeSelected", icon: MapNodeSelected },
    { id: "mapStructure", icon: MapStructure },
    { id: "mapStructureSelected", icon: MapStructureSelected },
  ];

  const mapLayerEvents = {
    click: mapItemClick,
    mouseover: (e: any) => (e.map.getCanvasContainer().style.cursor = "pointer"),
    mouseout: (e: any) => (e.map.getCanvasContainer().style.cursor = "grab"),
  };

  const customControls: [IAzureCustomControls] = [
    {
      control: new BringDataIntoViewControl(),
      controlOptions: { position: "top-right" } as ControlOptions,
    },
  ];

  useEffect(() => {
    const bounds = sewerObjectsMapStorage.boundingBox;
    if (bounds) {
      setMapZoomingInfo(addPaddingToBoundingCardinalPoints(bounds, 4));
    }
  }, [sewerObjectsMapStorage]);

  if (!configurationContent) return <LoadingSpinner />;

  return (
    <div className={`sewer-objects-map ${fullPage ? "full-page" : ""}`}>
      <AzureMapsProvider>
        <AzureMap
          options={azureMapOptions}
          controls={controls}
          customControls={customControls}
          LoaderComponent={LoadingSpinner}
          imageSprites={sewerObjectsImageSprites}
          events={{
            load: (e: any) => {
              for (const ds of e.map.sources["sources"].values()) {
                if (ds.id === "DataSource Provider") mapDataSourceRef.current = ds;
              }
              popup.attach(e.map);
              popup._addEventListener("close", deselectPreviousShape, false);
            },
            styledata: (e: MapDataEvent) =>
              sewerObjectsMapStorage.setCurrentMapStyles(e.map.getStyle().style),
          }}
        >
          <AzureMapDataSourceProvider id={"DataSource Provider"}>
            <AzureMapLayerProvider
              id={"shape AzureMapSectionLayer"}
              options={{
                filter: ["==", ["geometry-type"], "LineString"],
                strokeColor: "#ff8c00",
                strokeWidth: [
                  "case",
                  ["has", "isSelected"],
                  ["case", ["==", ["get", "isSelected"], true], 8, 5],
                  5,
                ],
                strokeOpacity: [
                  "case",
                  ["has", "isSelected"],
                  ["case", ["==", ["get", "isSelected"], true], 1, 0.7],
                  0.7,
                ],
              }}
              events={mapLayerEvents}
              type={"LineLayer"}
            />
            <AzureMapLayerProvider
              id={"shape AzureMapNodeStructureLayer"}
              options={{
                filter: ["all", ["==", ["geometry-type"], "Point"]],
                iconOptions: {
                  image: [
                    "match",
                    ["get", "mapObjectType"],
                    SewerObjectType.Node,
                    ["case", ["==", ["get", "isSelected"], true], "mapNodeSelected", "mapNode"],
                    SewerObjectType.Structure,
                    [
                      "case",
                      ["==", ["get", "isSelected"], true],
                      "mapStructureSelected",
                      "mapStructure",
                    ],
                    "none",
                  ],
                  anchor: "center",
                  allowOverlap: true,
                  ignorePlacement: true,
                  size: [
                    "case",
                    ["has", "isSelected"],
                    ["case", ["==", ["get", "isSelected"], true], 1, 0.7],
                    0.7,
                  ],
                },
              }}
              events={mapLayerEvents}
              type={"SymbolLayer"}
            />
            <AzureMapLayerProvider
              id={"shape AzureMapNodeStructureLayerPoint"}
              options={{
                offset: [0.0, 0.5],
                placement: "point",
                minZoom: 16,
                iconOptions: {
                  image: "none",
                },
                filter: ["==", ["geometry-type"], "Point"],
                textOptions: {
                  anchor: "bottom-left",
                  color: "#2799fe",
                  size: 12,
                  ignorePlacement: true,
                  offset: [0.5, -0.5],
                  textField: ["get", "mapObjectName"],
                },
              }}
              type={"SymbolLayer"}
            />
            <AzureMapLayerProvider
              id={"shape AzureMapSectionLayerLine"}
              options={{
                offset: [0.0, 0.5],
                placement: "line-center",
                minZoom: 18,
                iconOptions: {
                  image: "none",
                },
                filter: ["==", ["geometry-type"], "LineString"],
                textOptions: {
                  anchor: "bottom-left",
                  color: "#2799fe",
                  size: 12,
                  ignorePlacement: true,
                  offset: [0.5, -0.5],
                  textField: ["get", "mapObjectName"],
                },
              }}
              type={"SymbolLayer"}
            />
            {nodesAndStructures.map((mi) => (
              <AzureMapFeature
                key={mi.objectName + mi.mapShapeType}
                variant={"feature"}
                type={mi.mapShapeType}
                coordinate={mi.mapCoordinates[0]}
                properties={{
                  mapObjectName: mi.objectName,
                  mapObjectType: mi.objectType,
                  mapObjectId: mi.objectId,
                  details: mi.details,
                  color: "#00008B",
                }}
              />
            ))}
            {sections.map((mi) => (
              <AzureMapFeature
                key={mi.objectName + mi.mapShapeType}
                variant={"feature"}
                type={mi.mapShapeType}
                coordinates={mi.mapCoordinates}
                properties={{
                  mapObjectName: mi.objectName,
                  mapObjectType: mi.objectType,
                  mapObjectId: mi.objectId,
                  details: mi.details,
                  color: "#00008B",
                }}
              />
            ))}
          </AzureMapDataSourceProvider>
        </AzureMap>
      </AzureMapsProvider>
    </div>
  );
});
